import React, { useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import PatientSearchSelect from './PatientSearchSelect'
import { Phone, Mail, Calendar, Edit3, X as XIcon, FileText, UserPlus, UserCheck, Cake, Globe, User, ClipboardPlus, Mic, MessageCircle, Send, Trash2, Clock, Star } from 'lucide-react'
import { Link } from '@inertiajs/react'
import { toast } from 'sonner'
import Modal from './Modal'
import DictateButton from './DictateButton'

// Human labels for the intake "visit type" (Paul 2026-08-21). Keep in sync with IntakesController::VISIT_TYPES.
const VISIT_TYPE_LABELS = {
  checkup: 'Check-up & clean',
  pain: 'Pain / urgent problem',
  broken: 'Broken tooth / lost filling or crown',
  cosmetic: 'Cosmetic / smile makeover',
  implants: 'Implants / changing my smile',
  rootcanal_extraction: 'Root canal or extraction',
  unsure: 'Not sure / something else',
}


// ESTIMATE FOLLOW-UP from the diary (Paul 2026-09-09): "work from the diary or the estimates tracker and they
// are in sync". Same fields, same Delay presets and the SAME endpoint as the tracker's patient-row editor
// (PATCH /estimates/patient_tracking) — one note / reason / next follow-up for all of the patient's
// outstanding estimates. Auto-saves like the tracker.
const FOLLOWUP_DELAYS = [ [2, '2 days'], [7, '7 days'], [14, '2 weeks'], [28, '4 weeks'], [91, '3 months'] ]
function EstimateFollowUp({ appointment }) {
  const t = appointment.estimate_tracking
  const [note, setNote] = useState(t?.follow_up_notes || '')
  const noteBox = useRef(null)
  const [reason, setReason] = useState(t?.not_booked_reason || '')
  const [reasonNote, setReasonNote] = useState(t?.not_booked_reason_note || '')
  const [followUp, setFollowUp] = useState((t?.next_follow_up_at || '').slice(0, 10))
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const timer = useRef(null)
  const mounted = useRef(false)
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
  const save = (tracking, okMsg) => {
    setSaving(true)
    router.patch('/estimates/patient_tracking', { patient_id: appointment.patient_id, estimate_ids: t.estimate_ids, tracking }, {
      preserveScroll: true, preserveState: true, only: [ 'appointments', 'calendar_appointments' ],
      onSuccess: () => { setSavedAt(new Date()); if (okMsg) toast.success(okMsg) },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish: () => setSaving(false),
    })
  }
  const delay = (days, label) => {
    const d = new Date(); d.setDate(d.getDate() + days)
    const iso = d.toISOString().slice(0, 10)
    setFollowUp(iso)
    save({ next_follow_up_at: iso, logged_follow_up: true }, `Contact later — back on the tracker ${fmt(iso)} (${label})`)
  }
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => save({ follow_up_notes: note, not_booked_reason: reason, not_booked_reason_note: reasonNote, next_follow_up_at: followUp, logged_follow_up: true }), 800)
    return () => clearTimeout(timer.current)
  }, [ note, reason, reasonNote, followUp ])
  useEffect(() => () => clearTimeout(timer.current), [])
  if (!t || !t.count) return null
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/40 p-2.5 text-xs">
      <div className="mb-1 font-semibold text-brand-ink">
        Follow-up — {t.count} outstanding estimate{t.count === 1 ? '' : 's'}
        <span className="ml-1 font-normal text-brand-muted">({t.titles.join(' · ')}{t.count > t.titles.length ? ' …' : ''})</span>
        <Link href="/estimates" className="ml-2 font-normal text-brand-primary hover:underline">open tracker</Link>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-1 text-[10px] text-brand-muted">
        <span className="mr-0.5">Delay:</span>
        {FOLLOWUP_DELAYS.map(([days, label]) => (
          <button key={days} type="button" onClick={() => delay(days, label)}
            className="rounded border border-brand-border bg-white px-1.5 py-0.5 font-medium text-brand-ink hover:border-violet-400 hover:bg-violet-50 hover:text-violet-800"
            title={`Contact later — bring this patient back on the tracker in ${label}`}>{label}</button>
        ))}
        {followUp && <span className="ml-1">next follow-up {fmt(followUp)}</span>}
      </div>
      <label className="block text-[11px] font-medium text-brand-muted">What was last said (note)
        <textarea ref={noteBox} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Spoke 9 Sep — will book the crowns after month-end"
          className="mt-0.5 w-full rounded-md border border-brand-border bg-white px-2 py-1 text-xs" />
        <DictateButton targetRef={noteBox} onText={setNote} context="appointment_followup_note" className="mt-1" />
      </label>
      <div className="mt-1.5 grid grid-cols-2 gap-2">
        <label className="text-[11px] font-medium text-brand-muted">Reason not booked
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-0.5 w-full rounded-md border border-brand-border bg-white px-2 py-1 text-xs text-brand-ink">
            <option value="">— none —</option>
            {Object.entries(t.reasons || {}).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-medium text-brand-muted">Reason detail
          <input value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder="e.g. waiting for year-end bonus" className="mt-0.5 w-full rounded-md border border-brand-border bg-white px-2 py-1 text-xs" />
        </label>
      </div>
      <div className="mt-1 text-[10px]" aria-live="polite">
        {saving ? <span className="text-brand-muted">Saving…</span> : savedAt ? <span className="text-emerald-600">✓ Saved — the estimates tracker shows the same</span> : <span className="text-brand-muted">Changes save automatically to every outstanding estimate</span>}
      </div>
    </div>
  )
}

// One row of the Visit timeline (3 prev + this + 3 next). Green = they came, red = missed/cancelled,
// blue = booked ahead — so the attendance pattern reads at a glance (Paul 2026-08-24).
const TL_TONE = {
  completed: 'bg-emerald-100 text-emerald-700', closed: 'bg-emerald-100 text-emerald-700',
  arrived: 'bg-emerald-100 text-emerald-700', in_consultation: 'bg-emerald-100 text-emerald-700',
  no_show: 'bg-red-100 text-red-700', cancelled: 'bg-red-100 text-red-700',
  confirmed: 'bg-blue-100 text-blue-700', scheduled: 'bg-blue-100 text-blue-700',
  awaiting_confirmation: 'bg-amber-100 text-amber-700', pending_confirmation: 'bg-amber-100 text-amber-700',
}
function TimelineRow({ v, current }) {
  const d = v.date ? new Date(v.date) : null
  const dotCls = current ? 'bg-brand-primary' : v.attended ? 'bg-emerald-500' : v.missed ? 'bg-red-400' : 'bg-slate-300'
  return (
    <div className={`flex items-center gap-2 text-xs ${current ? 'font-semibold text-brand-ink' : ''}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${dotCls}`} />
      <span className="w-[4.5rem] shrink-0 tabular-nums text-brand-muted">{d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</span>
      <span className={`shrink-0 rounded px-1 py-px text-[9px] font-medium uppercase ${current ? 'bg-brand-primary/15 text-brand-primary' : (TL_TONE[v.status] || 'bg-slate-100 text-slate-500')}`}>{current ? 'this visit' : String(v.status || '').replace(/_/g, ' ')}</span>
      <span className="truncate text-brand-muted">{v.reason || ''}</span>
    </div>
  )
}

// ── Detail modal ────────────────────────────────────────────────────
// Click an event on the calendar → this pops open with the full patient
// + booking detail and the patient-journey status buttons. Reception
// drives a patient through the day from here:
//   Confirm → Arrived → In Consultation → Completed
// Each transition recolours the event on the calendar.
const STATUS_CHIP = {
  scheduled:            'bg-gray-100 text-gray-600',
  confirmed:            'bg-emerald-100 text-emerald-700',
  arrived:              'bg-yellow-100 text-yellow-800',
  in_consultation:      'bg-blue-100 text-blue-700',
  completed:            'bg-blue-700 text-white',
  cancelled:            'bg-red-100 text-red-700',
  no_show:              'bg-gray-100 text-gray-600',
  rescheduled:          'bg-violet-100 text-violet-700',
  pending_confirmation: 'bg-amber-100 text-amber-800',
}

// The front-desk journey, in order. Each button sets the status via
// /appointments/:id/set_status and tints the calendar event.
// "Start consultation" triggers ScribeSession.start_for under the hood
// (P9.4) — the receptionist doesn't need to know that.
const JOURNEY = [
  { key: 'confirmed',       label: 'Confirm',            active: 'bg-emerald-500' },
  { key: 'arrived',         label: 'Arrived',            active: 'bg-yellow-400 text-yellow-900' },
  { key: 'in_consultation', label: 'Start consultation', active: 'bg-blue-400', icon: Mic, hint: 'Scribe auto-starts' },
  { key: 'completed',       label: 'Completed',          active: 'bg-blue-700' },
]

const STATUS_LABEL = {
  scheduled: 'Scheduled', confirmed: 'Confirmed', arrived: 'Arrived',
  in_consultation: 'In Consultation', completed: 'Completed',
  cancelled: 'Cancelled', no_show: 'No show', rescheduled: 'Rescheduled',
  pending_confirmation: 'Awaiting Confirmation',
}

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })

const fmtDob = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000))
  return `${d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })} (age ${age})`
}

export default function AppointmentDetailModal({ appointment, open, onClose, onEdit, onCancel, onDelete }) {
  if (!appointment) return null

  // Fire-and-close: send the post-visit Google review request to this patient on WhatsApp.
  const sendReviewRequest = () => {
    router.post(`/appointments/${appointment.id}/whatsapp_request_review`, {}, {
      preserveScroll: true,
      onSuccess: () => { toast.success('Google review request sent'); onClose?.() },
      onError:   () => { toast.error('Could not send the review request'); onClose?.() },
    })
  }

  // Button in the WhatsApp section — ask first, then send.
  const requestReview = () => {
    if (!window.confirm(`Send a Google review request to ${appointment.patient_name || 'this patient'} on WhatsApp?`)) return
    sendReviewRequest()
  }

  // Nudge the patient about the estimate(s) we sent — "had time to look? can we help?" (Paul 2026-08-25).
  // Uses the approved estimate-followup template (delivers outside the 24h window). Ask first, then send.
  const estimateFollowup = () => {
    if (!window.confirm(`Send ${appointment.patient_name || 'this patient'} a WhatsApp asking if they've had time to look at their estimate and if we can help?`)) return
    router.post(`/appointments/${appointment.id}/whatsapp_estimate_followup`, {}, {
      preserveScroll: true,
      onSuccess: () => { toast.success('Estimate follow-up sent'); onClose?.() },
      onError:   () => { toast.error('Could not send the estimate follow-up'); onClose?.() },
    })
  }

  const setStatus = (status) => {
    router.patch(`/appointments/${appointment.id}/set_status`, { status }, {
      preserveScroll: true,
      onSuccess: () => {
        toast.success(`Marked as ${STATUS_LABEL[status] || status}`)
        // No review prompt on completion (Paul 2026-10-13): 5-star patients are asked personally on 071; the button stays.
        onClose?.()
      },
      onError: () => toast.error('Could not update status'),
    })
  }

  // Dismiss / restore the "Estimate to do" worklist badge for this visit (Paul 2026-07-29).
  const setEstimateNotNeeded = (value) => {
    router.patch(`/appointments/${appointment.id}/set_estimate_not_needed`, { value }, {
      preserveScroll: true,
      onSuccess: () => { toast.success(value ? 'Marked — no estimate needed' : 'Back on the estimate worklist'); onClose?.() },
      onError: () => toast.error('Could not update the estimate status'),
    })
  }

  // Send the practice's standard WhatsApp messages to this patient (reception-triggered).
  // kind = 'pack' (4 messages) or 'confirm' (booking confirmation only).
  const sendWhatsapp = (kind) => {
    const what = kind === 'pack' ? 'all 4 standard WhatsApp messages'
      : kind === 'intake' ? 'the secure intake form to complete'
      : 'the booking confirmation'
    if (!window.confirm(`Send ${what} to ${appointment.patient_name || 'this patient'} on WhatsApp?`)) return
    router.post(`/appointments/${appointment.id}/whatsapp_${kind}`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success('WhatsApp message(s) sent'),
      onError:   () => toast.error('Could not send WhatsApp message(s)'),
    })
  }

  // Ask the patient to CONFIRM (or reschedule) their booking via WhatsApp. On a successful send the
  // appointment turns yellow = "Awaiting confirmation"; the patient's reply then confirms it
  // ("CONFIRM APPOINTMENT") or starts a rebooking ("RESCHEDULE APPOINTMENT"). If the send fails
  // (patient outside the 24-hour window) the status is left unchanged — so no yellow = it didn't go.
  const requestConfirmation = () => {
    if (!window.confirm(`Ask ${appointment.patient_name || 'this patient'} to confirm their appointment on WhatsApp? It will show as "Awaiting confirmation" until they reply.`)) return
    router.post(`/appointments/${appointment.id}/whatsapp_request_confirmation`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; if (al) { toast.error(al); return } toast.success('Confirmation request sent'); onClose?.() },
      onError:   () => toast.error('Could not send the confirmation request'),
    })
  }

  // Link this new-patient booking to an EXISTING patient/account, right from the diary — so a
  // booking made as "new" that's really an existing patient (or a dependant of one) doesn't become
  // a duplicate. Merge = same person; Dependant = separate patient under the existing family account.
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkTarget, setLinkTarget] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [timeline, setTimeline] = useState(null) // { previous:[], upcoming:[], total } — 3 each side of THIS visit
  const targetName = linkTarget?.name || linkTarget?.full_name || 'this patient'
  const nameOf = (t) => t?.name || t?.full_name || 'this patient'
  // Return to the diary (where we are) so it reloads in place and the [New] tag flips to the account code.
  const backHere = () => (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/diary')

  // Auto-suggest likely existing duplicates (by ID/passport, then name) so reception links in ONE
  // tap instead of hunting in the search box. Only for a new-patient booking.
  useEffect(() => {
    if (!open || !appointment?.is_new_patient || !appointment?.patient_id) { setSuggestions([]); return }
    let live = true
    fetch(`/patients/${appointment.patient_id}/match_candidates`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then((d) => { if (live) setSuggestions(d.results || []) })
      .catch(() => {})
    return () => { live = false }
  }, [open, appointment?.patient_id, appointment?.is_new_patient])

  // Visit timeline — this patient's 3 previous + 3 next appointments around THIS one, so you can see at
  // a glance whether they're steadily working through a plan or have stopped coming (Paul 2026-08-24).
  useEffect(() => {
    if (!open || !appointment?.id) { setTimeline(null); return }
    let live = true
    setTimeline(null)
    fetch(`/appointments/${appointment.id}/visit_timeline`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then((d) => { if (live) setTimeline(d) })
      .catch(() => {})
    return () => { live = false }
  }, [open, appointment?.id])

  const doMerge = (t) => {
    if (!t) return
    if (!window.confirm(`This booking is actually ${nameOf(t)}? Merge ${appointment.patient_name} INTO ${nameOf(t)} — the appointment moves onto their record and the duplicate is removed.`)) return
    router.post(`/patients/${appointment.patient_id}/merge_into`, { target_id: t.id, return_to: backHere() },
      { preserveScroll: true, onSuccess: () => { toast.success(`Linked to ${nameOf(t)}`); onClose?.() } })
  }
  const doDependant = (t) => {
    if (!t) return
    if (!window.confirm(`Add ${appointment.patient_name} as a DEPENDANT under ${nameOf(t)}'s account? They stay a separate patient, but billing rolls up to that family account — no new account is created.`)) return
    router.post(`/patients/${appointment.patient_id}/link_as_dependant`, { target_id: t.id, return_to: backHere() },
      { preserveScroll: true, onSuccess: () => { toast.success(`Linked as dependant of ${nameOf(t)}`); onClose?.() } })
  }
  const mergePatient = () => doMerge(linkTarget)
  const dependantPatient = () => doDependant(linkTarget)

  const dob = fmtDob(appointment.patient_dob)
  const isNew = appointment.is_new_patient

  // Child-visit nudge: the reason reads like a child's visit but it's on the parent, and a minor
  // dependant already exists on the account. One tap moves it onto the child (guardian retained).
  const childSuggestion = appointment.suggested_dependant
  const assignToDependant = () => {
    if (!childSuggestion) return
    if (!window.confirm(`This visit reads like ${childSuggestion.name}'s (age ${childSuggestion.age}). Move the appointment onto ${childSuggestion.name}? ${appointment.patient_name} stays the guardian and still receives the reminders.`)) return
    router.patch(`/appointments/${appointment.id}/assign_dependant`, { dependant_id: childSuggestion.id }, {
      preserveScroll: true,
      onSuccess: () => onClose?.(),
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Appointment Details"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-brand-danger transition-colors hover:bg-brand-danger/10"
            >
              <XIcon size={15} /> Cancel
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                title="Permanently remove this appointment from the diary"
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-brand-danger transition-colors hover:bg-brand-danger/10"
              >
                <Trash2 size={15} /> Delete
              </button>
            )}
          </div>
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-surface/45"
          >
            <Edit3 size={15} /> Edit
          </button>
        </div>
      }
    >
      {/* Patient card */}
      <div className="mb-4 rounded-xl border border-brand-accent/75 bg-gradient-to-br from-brand-surface/35 to-white p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-brand-primary">
            <span className="text-white font-semibold">
              {(appointment.patient_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-lg font-semibold text-brand-ink">{appointment.patient_name}</h3>
              <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isNew ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'}`}>
                {isNew ? <><UserPlus size={11} /> New patient</> : <><UserCheck size={11} /> Returning</>}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-brand-muted">
              {/* Dentists don't see patient contact (Paul 2026-08-26, POPIA) — shown as 🔒 Hidden. */}
              {appointment.patient_contact_hidden ? (
                <span className="flex items-center gap-1.5"><Phone size={13} /> 🔒 Hidden</span>
              ) : (
                appointment.patient_phone && (
                  <span className="flex items-center gap-1.5"><Phone size={13} /> {appointment.patient_phone}</span>
                )
              )}
              {!appointment.patient_contact_hidden && appointment.patient_email && (
                <span className="flex items-center gap-1.5"><Mail size={13} /> {appointment.patient_email}</span>
              )}
            </div>
          </div>
          <span className={`inline-flex flex-shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CHIP[appointment.status] || 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABEL[appointment.status] || appointment.status}
          </span>
        </div>
      </div>

      {/* Child-visit nudge — this booking reads like a child's visit but sits on the parent. */}
      {childSuggestion && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50/70 p-3">
          <p className="text-sm text-amber-900">
            👶 This looks like a child's visit — <strong>{childSuggestion.name}</strong> (age {childSuggestion.age}) is a dependant on this account, but the appointment is on <strong>{appointment.patient_name}</strong>.
          </p>
          <button
            onClick={assignToDependant}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-amber-600"
          >
            Assign this visit to {childSuggestion.name}
          </button>
        </div>
      )}

      {/* New-patient linking — avoid duplicates: link to an existing patient/account at the diary. */}
      {isNew && appointment.patient_id && (
        <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
          {/* One-tap suggestions: existing patients this booking most likely duplicates (matched on
              ID/passport, then name). Reception confirms with a single click — no hunting. */}
          {suggestions.length > 0 && (
            <div className="mb-2 space-y-2">
              <p className="text-xs font-semibold text-indigo-900">Looks like an existing patient — link in one tap:</p>
              {suggestions.map((s) => (
                <div key={s.id} className="rounded-lg border border-indigo-200 bg-white p-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-brand-ink">
                        {s.name}{s.account_code ? <span className="ml-1 text-brand-primary">[{s.account_code}]</span> : null}
                      </p>
                      <p className="truncate text-[11px] text-brand-muted">
                        <span className="font-medium text-indigo-700">{s.reason}</span>
                        {s.id_number ? ` · ID ${s.id_number}` : ''}{s.account_code ? ` · Acct ${s.account_code}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 gap-1.5">
                      <button type="button" onClick={() => doMerge(s)} className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-700" title="Same person — merge this booking into their record">
                        <UserCheck size={12} /> Same person
                      </button>
                      <button type="button" onClick={() => doDependant(s)} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700" title="Add as a dependant under their family account">
                        <UserPlus size={12} /> Dependant
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-brand-muted">Not one of these? Search below.</p>
            </div>
          )}
          {!linkOpen ? (
            <button type="button" onClick={() => setLinkOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700 transition-colors hover:text-indigo-900">
              <UserCheck size={14} /> Link to an existing patient / account…
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-indigo-900">Is this really an existing patient, or a dependant of one? Search and link — no duplicate account is created.</p>
              <PatientSearchSelect selected={linkTarget} onSelect={setLinkTarget} excludeId={appointment.patient_id} />
              {linkTarget && (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={mergePatient} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700">
                    <UserCheck size={13} /> Same person — merge
                  </button>
                  <button type="button" onClick={dependantPatient} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700">
                    <UserPlus size={13} /> Add as dependant of {targetName}
                  </button>
                </div>
              )}
              <button type="button" onClick={() => { setLinkOpen(false); setLinkTarget(null) }} className="block text-xs text-brand-muted transition-colors hover:text-brand-ink">Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* Patient journey — one-click status buttons */}
      <div className="mb-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Patient journey</h4>
        <div className="grid grid-cols-4 gap-2">
          {JOURNEY.map((step) => {
            const isCurrent = appointment.status === step.key
            const StepIcon = step.icon
            return (
              <button
                key={step.key}
                onClick={() => setStatus(step.key)}
                title={step.hint || undefined}
                className={`flex items-center justify-center gap-1 rounded-xl px-2 py-2.5 text-xs font-semibold transition-colors ${
                  isCurrent
                    ? `${step.active} ${step.key === 'arrived' ? '' : 'text-white'} shadow-sm`
                    : 'border border-brand-border bg-white text-brand-ink hover:bg-brand-surface/50'
                }`}
              >
                {StepIcon && <StepIcon size={12} />}
                {step.label}
              </button>
            )
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-brand-muted">
          Tap to update — the colour on the calendar changes to match.
        </p>
      </div>

      {/* NO MOBILE NUMBER = NO WHATSAPP, AND SAY SO (Paul 2026-07-14).
          This whole section used to just VANISH when the patient had no phone, with no explanation — so
          it looked like the feature was missing from that dentist's diary, when in fact the patient
          simply had no number on file. A patient with no mobile silently receives NOTHING: no booking
          confirmation, no reminder, no intake form, no treatment plan. Say it out loud and offer the fix. */}
      {!appointment.patient_phone && !appointment.patient_contact_hidden && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">WhatsApp the patient</h4>
          <p className="text-[13px] text-amber-900">
            <strong>No mobile number on file.</strong> We cannot WhatsApp {appointment.patient_name || 'this patient'} —
            there is no number on their record and none on their account head's either, so they will get no
            booking confirmation, no reminder, no intake form and no treatment plan.
          </p>
          {appointment.patient_id && (
            <a href={`/patients/${appointment.patient_id}`}
               className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-600 bg-white px-2.5 py-1.5 text-[13px] font-semibold text-amber-800 hover:bg-amber-100">
              Add a mobile number →
            </a>
          )}
        </div>
      )}

      {/* WhatsApp standard messages — reception sends the practice's standard pack or just the confirmation */}
      {appointment.patient_phone && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">WhatsApp the patient</h4>
          {/* Whose number this is. A child is messaged via their parent (Patient#contact_phone), so
              reception must see the real recipient rather than assume they're texting a 6-year-old. */}
          {appointment.patient_phone_via && (
            <p className="mb-2 text-[12px] text-brand-muted">
              Messages go to <strong>{appointment.patient_phone_via.name}</strong>{' '}
              ({appointment.patient_phone_via.relationship}) · {appointment.patient_phone}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => sendWhatsapp('pack')}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-2.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
            >
              <MessageCircle size={13} /> Send 4 standard messages
            </button>
            <button
              onClick={() => sendWhatsapp('confirm')}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-white px-2 py-2.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
            >
              <Send size={13} /> Send booking confirmation
            </button>
          </div>
          <button
            onClick={() => sendWhatsapp('intake')}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-sky-300 bg-sky-50 px-2 py-2.5 text-xs font-semibold text-sky-700 transition-colors hover:bg-sky-100"
          >
            <ClipboardPlus size={13} /> Send intake form to complete
          </button>
          <button
            onClick={requestConfirmation}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-2 py-2.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
          >
            <Clock size={13} /> Request booking confirmation
          </button>
          <button
            onClick={requestReview}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-yellow-300 bg-yellow-50 px-2 py-2.5 text-xs font-semibold text-yellow-800 transition-colors hover:bg-yellow-100"
          >
            <Star size={13} /> Request Google review
          </button>
          <button
            onClick={estimateFollowup}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-indigo-300 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-800 transition-colors hover:bg-indigo-100"
          >
            <FileText size={13} /> Check in on estimate
          </button>
          <p className="mt-1.5 text-[11px] text-brand-muted">
            Pack = location, directions, intake form + the booking confirmation. Or send just the confirmation, or just the <strong>intake form</strong> to complete.
            <br /><strong>Request</strong> asks the patient to reply Confirm or Reschedule — the slot turns yellow (<em>Awaiting confirmation</em>) until they do.
            <br /><strong>Review</strong> sends the post-visit Google review request (auto-offered when you mark the visit Completed).
            <br /><strong>Check in on estimate</strong> asks the patient — on the approved template — if they've had time to look at the estimate we sent and whether we can help.
          </p>
        </div>
      )}

      {/* Booking info */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-brand-ink">Booking information</h4>
        <div className="space-y-3 rounded-xl border border-brand-accent/75 p-4">
          <Row icon={Calendar} label="Date">{fmtDate(appointment.start_time)}</Row>
          <Row icon={Calendar} label="Time">{fmtTime(appointment.start_time)} — {fmtTime(appointment.end_time)}</Row>
          {/* WHY they're coming. Existing patient booked for work we quoted → the linked estimate
              (its "Visit N — …" title) with a deep-link to the estimate. New patient / no estimate →
              the free-text reason they gave at booking. */}
          {appointment.estimate_summary ? (
            <Row icon={FileText} label="Booked for estimate">
              {appointment.estimate_id ? (
                <Link href={`/estimates/${appointment.estimate_id}`} onClick={onClose} className="font-medium text-brand-primary hover:underline">
                  {appointment.estimate_summary} →
                </Link>
              ) : appointment.estimate_summary}
              {appointment.reason && <span className="mt-0.5 block text-xs text-brand-muted">Patient's note: {appointment.reason}</span>}
            </Row>
          ) : (
            <Row icon={FileText} label="Reason for visit">{appointment.reason || '—'}</Row>
          )}
          {/* Reason-for-visit the patient gave on the online intake (Paul 2026-08-21): their OWN WORDS,
              a visit-type chip, an urgency flag, and a "→ Dr Chalita" nudge for cosmetic/implant work. */}
          {(appointment.patient_stated_reason || appointment.visit_type || appointment.visit_urgency) && (
            <div className="rounded-lg border border-brand-border bg-brand-surface/60 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-muted">
                <FileText className="h-3.5 w-3.5" /> From their intake form
              </div>
              {appointment.patient_stated_reason && (
                <p className="mt-1.5 text-sm italic text-brand-ink">“{appointment.patient_stated_reason}”</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {appointment.visit_type && (
                  <span className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-[11px] font-medium text-brand-primary">
                    {VISIT_TYPE_LABELS[appointment.visit_type] || appointment.visit_type}
                  </span>
                )}
                {appointment.visit_urgency && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">⚠ {appointment.visit_urgency}</span>
                )}
                {appointment.visit_needs_chalita && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">→ Dr Chalita (cosmetic / implant)</span>
                )}
              </div>
            </div>
          )}
          {/* Visit timeline (Paul 2026-08-24): 3 previous + this + 3 next, so you can see whether the
              patient is steadily working through a plan or has stopped coming — not just the next date. */}
          {timeline && (
            <div className="mt-1 rounded-xl border border-brand-border bg-brand-surface/30 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-muted">
                <Calendar className="h-3.5 w-3.5" /> Visit timeline
                {typeof timeline.total === 'number' && <span className="font-normal normal-case text-brand-muted/70">· {timeline.total} total</span>}
              </p>
              <div className="space-y-1">
                {(timeline.previous || []).length === 0 && <p className="text-xs text-brand-muted">No earlier appointments — this is their first visit.</p>}
                {(timeline.previous || []).map((v) => <TimelineRow key={v.id} v={v} />)}
                <TimelineRow v={{ date: appointment.start_time, status: appointment.status, reason: appointment.reason }} current />
                {(timeline.upcoming || []).map((v) => <TimelineRow key={v.id} v={v} />)}
                {(timeline.upcoming || []).length === 0 && <p className="text-xs text-amber-700">No follow-up booked yet — check they haven't dropped off.</p>}
              </div>
            </div>
          )}
          {dob && <Row icon={Cake} label="Date of birth">{dob}</Row>}
          {appointment.patient_language && (
            <Row icon={Globe} label="Preferred language">
              {appointment.patient_language === 'af' ? 'Afrikaans' : 'English'}
            </Row>
          )}
          {appointment.notes && (
            <Row icon={FileText} label="Notes"><span className="whitespace-pre-wrap">{appointment.notes}</span></Row>
          )}
          {/* ESTIMATE-STATUS worklist (Paul 2026-07-29) — only once the patient has been SEEN. Lets the day
              be scanned for who still needs an estimate; "No estimate needed" dismisses it for this visit. */}
          {appointment.estimate_status && (
            <Row icon={ClipboardPlus} label="Estimate">
              {appointment.estimate_status === 'needed' && (
                <div className="space-y-1.5">
                  <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">📝 Estimate still to do</span>
                  <button type="button" onClick={() => setEstimateNotNeeded(true)}
                    className="block text-xs font-medium text-brand-muted underline hover:text-brand-ink">
                    No estimate needed for this visit
                  </button>
                </div>
              )}
              {appointment.estimate_status === 'to_send' && (
                <div className="space-y-1.5">
                  <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">📤 Estimate drafted — not yet sent</span>
                  <span className="block text-xs text-brand-muted">Review it with Dr Chalita, then send it to the patient today.</span>
                  <button type="button" onClick={() => setEstimateNotNeeded(true)}
                    className="block text-xs font-medium text-brand-muted underline hover:text-brand-ink">
                    No estimate needed for this visit
                  </button>
                </div>
              )}
              {appointment.estimate_status === 'done' && (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">✓ Estimate sent to the patient</span>
              )}
              {appointment.estimate_status === 'case_sent' && (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">✓ Case of estimates sent to the patient</span>
              )}
              {appointment.estimate_status === 'not_needed' && (
                <div className="space-y-1.5">
                  <span className="inline-flex items-center gap-1 rounded bg-brand-surface px-2 py-0.5 text-xs font-medium text-brand-muted">No estimate needed</span>
                  <button type="button" onClick={() => setEstimateNotNeeded(false)}
                    className="block text-xs font-medium text-brand-muted underline hover:text-brand-ink">
                    Put back on the estimate worklist
                  </button>
                </div>
              )}
            </Row>
          )}
          {appointment.estimate_tracking?.count > 0 && (
            <Row icon={ClipboardPlus} label="Follow-up">
              <EstimateFollowUp key={`fu-${appointment.id}`} appointment={appointment} />
            </Row>
          )}
          {/* What was transcribed / discussed in the chair (scribe digest) — "what they've spoken". */}
          {appointment.scribe_summary && (
            <Row icon={Mic} label="From the visit">
              <div className="space-y-1 text-xs text-brand-ink">
                {appointment.scribe_summary.decisions && <div><span className="font-semibold">Discussed / decided:</span> {appointment.scribe_summary.decisions}</div>}
                {appointment.scribe_summary.estimate_intent && <div><span className="font-semibold">Estimate intent:</span> {appointment.scribe_summary.estimate_intent}</div>}
                {Array.isArray(appointment.scribe_summary.questions) && appointment.scribe_summary.questions.length > 0 && (
                  <div><span className="font-semibold">Patient asked:</span> {appointment.scribe_summary.questions.join('; ')}</div>
                )}
              </div>
            </Row>
          )}
        </div>
      </div>

      {/* Context-aware primary action: one click from the booking straight into a
          new estimate — which then pre-suggests the codes for this visit reason. */}
      <button
        type="button"
        onClick={() => { router.post(`/patients/${appointment.patient_id}/estimates`); onClose?.() }}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-3 py-2.5 text-sm font-semibold text-white hover:bg-brand-primary-dark"
      >
        <FileText size={15} /> Start estimate for this visit
      </button>

      {/* R3 — quick links so reception/dentist can jump from a calendar
          pop-over to the patient's profile or active treatment plan
          without backtracking through the patients list. */}
      <button
        type="button"
        onClick={() => { router.post(`/patients/${appointment.patient_id}/courses_of_treatment`); onClose?.() }}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-brand-ink bg-brand-ink px-3 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        title="Open this patient's tooth chart and treatment plan — reuses their open plan, or starts one"
      >
        <ClipboardPlus size={15} /> Dental charting
      </button>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <Link
          href={`/patients/${appointment.patient_id}`}
          onClick={onClose}
          className="group flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm font-medium text-brand-ink hover:bg-brand-surface"
        >
          <span className="flex items-center gap-2"><User size={14} /> Open patient profile</span>
          <span className="text-brand-muted group-hover:text-brand-ink">→</span>
        </Link>
        <Link
          href={`/courses-of-treatment?patient_id=${appointment.patient_id}`}
          onClick={onClose}
          className="group flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm font-medium text-brand-ink hover:bg-brand-surface"
        >
          <span className="flex items-center gap-2"><ClipboardPlus size={14} /> View treatment plans</span>
          <span className="text-brand-muted group-hover:text-brand-ink">→</span>
        </Link>
      </div>
    </Modal>
  )
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={15} className="mt-0.5 flex-shrink-0 text-brand-primary" />
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{label}</p>
        <p className="mt-0.5 text-sm text-brand-ink">{children}</p>
      </div>
    </div>
  )
}
